import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseReelPlaybackReturn {
  currentIndex: number;
  isPaused: boolean;
  isComplete: boolean;
  togglePause: () => void;
  reset: () => void;
  /** Call when narration starts — holds advancement until narration ends */
  holdAdvance: () => void;
  /** Call when narration ends — releases the hold and advances if timer already expired */
  releaseAdvance: () => void;
}

/**
 * Custom hook for managing fashion show reel slideshow playback.
 *
 * Uses a setTimeout-chain approach (not setInterval) so that each outfit's
 * minimum display timer starts fresh after the previous outfit's narration
 * completes. This ensures:
 *
 *   effective display time per outfit = max(intervalMs, narration duration)
 *
 * The hold/release mechanism coordinates with the narration hook:
 * - holdAdvance() is called synchronously when narration fetch begins
 * - releaseAdvance() is called when narration audio finishes (or fails)
 * - If the minimum timer expires while held, advancement is deferred
 *   until releaseAdvance() fires, then a fresh timer starts for the next outfit
 *
 * Starts with hold active for the first outfit so the timer cannot advance
 * before narration is ready (critical for cold Lambda starts).
 *
 * Requirements: 3.1, 3.3, 5.1, 5.3, 5.4
 */
export function useReelPlayback(
  totalOutfits: number,
  intervalMs: number,
): UseReelPlaybackReturn {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingTimeRef = useRef<number>(intervalMs);
  const lastTickTimeRef = useRef<number>(Date.now());
  const mountedRef = useRef<boolean>(true);
  const isPausedRef = useRef<boolean>(false);

  // Narration hold state: when true, timer expiry is deferred until released.
  // Starts as TRUE for the first outfit — narration hook calls holdAdvance()
  // immediately when playForOutfit is invoked, and releaseAdvance() when done.
  const isHeldRef = useRef<boolean>(true);
  const timerExpiredWhileHeldRef = useRef<boolean>(false);

  const currentIndexRef = useRef<number>(currentIndex);
  currentIndexRef.current = currentIndex;

  const totalOutfitsRef = useRef<number>(totalOutfits);
  totalOutfitsRef.current = totalOutfits;

  const isCompleteRef = useRef<boolean>(isComplete);
  isCompleteRef.current = isComplete;

  const intervalMsRef = useRef<number>(intervalMs);
  intervalMsRef.current = intervalMs;

  // Use a ref for the advance function to break the circular dependency
  // between scheduleTimer and doAdvance (they reference each other).
  const doAdvanceRef = useRef<() => void>(() => {});

  const clearTimer = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  /**
   * Schedule the minimum display timer for the current outfit.
   * Uses setTimeout (not setInterval) so each outfit gets a fresh timer.
   * When the timer fires, it either advances or defers if narration is held.
   */
  const scheduleTimer = useCallback((durationMs: number) => {
    clearTimer();
    lastTickTimeRef.current = Date.now();
    remainingTimeRef.current = durationMs;

    timeoutIdRef.current = setTimeout(() => {
      if (!mountedRef.current || isCompleteRef.current || isPausedRef.current) return;

      // Timer expired — check if narration is still playing
      if (isHeldRef.current) {
        timerExpiredWhileHeldRef.current = true;
        console.log('[useReelPlayback] Timer expired but narration hold is active — deferring advance');
        return;
      }

      // Narration already done, advance now
      doAdvanceRef.current();
    }, durationMs);
  }, [clearTimer]);

  /**
   * Perform the actual advancement to the next outfit.
   * Clears the old timer, updates the index, and schedules a fresh timer
   * for the new outfit. Sets hold active so the new outfit's narration
   * has time to establish its hold before the timer can fire.
   */
  const doAdvance = useCallback(() => {
    if (!mountedRef.current || isCompleteRef.current) return;

    const nextIndex = currentIndexRef.current + 1;

    if (nextIndex >= totalOutfitsRef.current) {
      clearTimer();
      setIsComplete(true);
      console.log('[useReelPlayback] Last outfit done, completing reel');
    } else {
      // Set hold active for the new outfit BEFORE updating index.
      // The narration useEffect will fire when currentIndex changes,
      // calling playForOutfit which calls holdAdvance (idempotent).
      isHeldRef.current = true;
      timerExpiredWhileHeldRef.current = false;

      setCurrentIndex(nextIndex);
      console.log(`[useReelPlayback] Advanced to outfit ${nextIndex}, hold active for new narration`);

      // Schedule a fresh minimum display timer for the new outfit.
      // If narration takes longer than intervalMs, the hold mechanism
      // will defer advancement until narration completes.
      scheduleTimer(intervalMsRef.current);
    }
  }, [clearTimer, scheduleTimer]);

  // Keep the ref in sync so scheduleTimer's setTimeout callback always
  // calls the latest version of doAdvance (breaks circular dependency).
  doAdvanceRef.current = doAdvance;

  /**
   * Start playback from the current position.
   * Schedules the first timer. The hold is already active (set in init/reset),
   * so even if the timer fires before narration starts, it will defer.
   */
  const startPlayback = useCallback(() => {
    if (totalOutfitsRef.current <= 0) {
      setIsComplete(true);
      return;
    }
    scheduleTimer(intervalMsRef.current);
  }, [scheduleTimer]);

  /**
   * Hold advancement — called when narration fetch begins for an outfit.
   * If the timer fires while held, advancement is deferred until released.
   * Idempotent: safe to call multiple times (e.g., initial hold + narration hold).
   */
  const holdAdvance = useCallback(() => {
    isHeldRef.current = true;
    console.log('[useReelPlayback] Hold activated');
  }, []);

  /**
   * Release the hold — called when narration finishes (audio ended, error, or fetch failed).
   * If the timer already expired while held, advance immediately and schedule
   * a fresh timer for the next outfit.
   */
  const releaseAdvance = useCallback(() => {
    isHeldRef.current = false;
    console.log(`[useReelPlayback] Hold released, timerExpired=${timerExpiredWhileHeldRef.current}`);

    if (timerExpiredWhileHeldRef.current) {
      timerExpiredWhileHeldRef.current = false;
      doAdvanceRef.current();
    }
    // If timer hasn't expired yet, it will fire naturally via the scheduled setTimeout
    // and advance at that point (since hold is now released).
  }, []);

  const togglePause = useCallback(() => {
    if (!mountedRef.current || isCompleteRef.current) return;

    setIsPaused((prev) => {
      const wasPaused = prev;

      if (!wasPaused) {
        // PAUSING: record remaining time and clear timer
        const elapsed = Date.now() - lastTickTimeRef.current;
        remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
        clearTimer();
        isPausedRef.current = true;
      } else {
        // RESUMING: schedule timer with remaining time for current slide
        isPausedRef.current = false;
        const remaining = remainingTimeRef.current;

        if (remaining > 0) {
          scheduleTimer(remaining);
        } else {
          // Timer already expired while paused — check hold
          if (isHeldRef.current) {
            timerExpiredWhileHeldRef.current = true;
          } else {
            doAdvanceRef.current();
          }
        }
      }

      return !wasPaused;
    });
  }, [clearTimer, scheduleTimer]);

  const reset = useCallback(() => {
    clearTimer();
    setCurrentIndex(0);
    setIsPaused(false);
    isPausedRef.current = false;
    setIsComplete(false);
    remainingTimeRef.current = intervalMs;
    lastTickTimeRef.current = Date.now();
    // Start held — narration for the first outfit will establish its hold
    isHeldRef.current = true;
    timerExpiredWhileHeldRef.current = false;
  }, [intervalMs, clearTimer]);

  // Start playback on mount (and when totalOutfits/intervalMs change)
  useEffect(() => {
    if (!isPaused && !isComplete && totalOutfits > 0) {
      startPlayback();
    }

    return () => {
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalOutfits, intervalMs]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  return {
    currentIndex,
    isPaused,
    isComplete,
    togglePause,
    reset,
    holdAdvance,
    releaseAdvance,
  };
}
